// "Search part codes only" preference.
//
// Some workshops identify parts by code and treat name/description matches as
// noise. The toggle is one shared setting rather than one per dialog: it says
// how this workshop searches, so flipping it in the counter sale must also
// apply in the work-order dialog, the invoice line dialog and the list views.
//
// Persisted per organization (a user can belong to several, and only some of
// them work this way). All localStorage access goes through here so the
// try/catch lives in one place and nothing can throw — a blocked or full
// localStorage just means the toggle does not stick.
import { useCallback, useEffect, useState } from 'react';

import { useUserStore } from '../store';

const STORAGE_PREFIX = 'partsSearchCodeOnly';

// Same-tab fan-out: the native 'storage' event only fires in *other* tabs, so
// mounted surfaces in this tab would otherwise keep a stale toggle.
const CHANGE_EVENT = 'verstaani:part-code-only';

function storageKey(orgId) {
  return orgId ? `${STORAGE_PREFIX}:${orgId}` : null;
}

/** Read the stored flag. Defaults to false (search everything, as before). */
export function getPartCodeOnly(orgId) {
  const key = storageKey(orgId);
  if (!key) {
    return false;
  }
  try {
    return window.localStorage.getItem(key) === 'true';
  } catch (e) {
    return false;
  }
}

/** Persist the flag and notify every mounted surface in this tab. */
export function setPartCodeOnly(orgId, value) {
  const key = storageKey(orgId);
  if (!key) {
    return;
  }
  try {
    window.localStorage.setItem(key, String(value));
  } catch (e) {
    // ignore — the toggle still applies for this session
  }
  try {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { orgId, value } }));
  } catch (e) {
    // ignore
  }
}

/**
 * [codeOnly, setCodeOnly] for the current organization, kept in sync with any
 * other component using the hook.
 */
export function usePartCodeOnly() {
  const { currentOrganization } = useUserStore();
  const orgId = currentOrganization?.organization_id;
  const [codeOnly, setCodeOnlyState] = useState(() => getPartCodeOnly(orgId));

  useEffect(() => {
    setCodeOnlyState(getPartCodeOnly(orgId));
  }, [orgId]);

  useEffect(() => {
    const handler = (event) => {
      if (event?.detail?.orgId !== orgId) {
        return;
      }
      setCodeOnlyState(!!event.detail.value);
    };
    window.addEventListener(CHANGE_EVENT, handler);
    return () => window.removeEventListener(CHANGE_EVENT, handler);
  }, [orgId]);

  const setCodeOnly = useCallback(
    (value) => {
      setCodeOnlyState(value);
      setPartCodeOnly(orgId, value);
    },
    [orgId]
  );

  return [codeOnly, setCodeOnly];
}
